// M04 /diagnosticos/modelos: institution-scoped questionnaire models and versions (BLD-26).
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Layers } from "lucide-react";
import { Alert, Button, EmptyState, ErrorState, PageHeader, Panel, Pill, SelectField, Skeleton, TextField } from "../../components/ui.tsx";
import { Modal } from "../../components/dialog.tsx";
import { GuardedLink } from "../../components/plan-shell.tsx";
import { useToast } from "../../components/toast.tsx";
import { ApiError, patch, post } from "../../lib/api.ts";
import { useGuardedNavigate } from "../../lib/guard.tsx";
import { usePageTitle } from "../../lib/nav.tsx";
import { mBase, mKey, withInstitution, type MaturityInstitution, type TemplateSummary, type VersionDetail } from "../../lib/maturity.ts";
import { MaturityLayout, ModuleNav, WithInstitution } from "./MaturityLayout.tsx";
import { useTemplates } from "./AssessmentsPage.tsx";
import { formatInstantBR } from "../../../shared/dates.ts";
import { MATURITY_LIMITS } from "../../../shared/maturity/definition.ts";

export function TemplatesPage() {
  usePageTitle("Modelos de questionário");
  return <WithInstitution>{(inst, all) => <TemplatesBody institution={inst} all={all} />}</WithInstitution>;
}

function TemplatesBody({ institution, all }: { institution: MaturityInstitution; all: MaturityInstitution[] }) {
  const q = useTemplates(institution.id);
  const qc = useQueryClient();
  const toast = useToast();
  const go = useGuardedNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const base = mBase(institution.id);
  const refresh = () => qc.invalidateQueries({ queryKey: mKey(institution.id, "templates") });
  const editorHref = (t: string, v: string) => withInstitution(`/diagnosticos/modelos/${t}/versoes/${v}`, institution.id);

  async function act(label: string, fn: () => Promise<unknown>, ok: string) {
    setBusy(label);
    try {
      await fn();
      await refresh();
      toast("success", ok);
    } catch (e) {
      toast("danger", e instanceof Error ? e.message : "Não foi possível concluir a operação.");
    } finally {
      setBusy(null);
    }
  }
  const templates = q.data?.data ?? [];
  const hasStarter = templates.some((t) => t.source_key === "gp-starter-v1");

  return (
    <MaturityLayout>
      <PageHeader eyebrow={institution.name} title="Diagnóstico institucional" subtitle="Questionários desta instituição. Versões publicadas não mudam; para editar, crie uma nova versão."
        actions={<>
          {!hasStarter && !q.isPending && <Button variant="secondary" loading={busy === "starter"} onClick={() => void act("starter", () => post(`${base}/templates/from-starter`, { source_key: "gp-starter-v1" }), "Questionário inicial carregado.")}>Carregar questionário inicial</Button>}
          <Button onClick={() => setCreating(true)}>Novo modelo</Button>
        </>} />
      <ModuleNav institution={institution} all={all} />
      {creating && <NewTemplateDialog institution={institution} templates={templates} onClose={() => setCreating(false)} onCreated={(t) => go(editorHref(t.id, t.versions[0]!.id))} />}
      {q.isPending ? <Skeleton lines={4} /> : q.isError ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : templates.length === 0 ? (
        <EmptyState icon={Layers} title="Nenhum questionário nesta instituição."
          action={<Button loading={busy === "starter"} onClick={() => void act("starter", () => post(`${base}/templates/from-starter`, { source_key: "gp-starter-v1" }), "Questionário inicial carregado.")}>Carregar questionário inicial</Button>}>
          O questionário inicial de Gestão de Pessoas (6 subdimensões, 16 categorias, 78 quesitos) é copiado para esta instituição, sem respostas.
        </EmptyState>
      ) : (
        <ul className="space-y-4">
          {templates.map((t) => (
            <li key={t.id}>
              <Panel as="article">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-bold">{t.name}</h2>
                    <p className="text-xs text-muted">{t.source_key === "gp-starter-v1" ? "Questionário inicial de Gestão de Pessoas" : "Modelo próprio"}</p>
                  </div>
                  <Button variant="ghost" loading={busy === `retire-${t.id}`}
                    onClick={() => void act(`retire-${t.id}`, () => patch(`${base}/templates/${t.id}`, { expected_version: t.version, retired: true }), "Modelo retirado: ele não aparece em novas avaliações; o histórico continua disponível.")}>
                    Retirar modelo
                  </Button>
                </div>
                <ul className="mt-3 divide-y divide-line rounded-ctl border border-line">
                  {t.versions.slice().reverse().map((v) => (
                    <li key={v.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5 text-sm">
                      <span className="font-semibold">Versão {v.revision_number}</span>
                      {v.state === "published" ? <Pill>Publicada</Pill> : <Pill tone="gold">Rascunho</Pill>}
                      <span className="num text-xs text-muted">{v.question_count} quesitos · {v.assessment_count} avaliações{v.published_at ? ` · publicada em ${formatInstantBR(v.published_at)}` : ""}</span>
                      <span className="ml-auto flex flex-wrap gap-2">
                        <GuardedLink href={editorHref(t.id, v.id)} className="elevate inline-flex min-h-9 items-center rounded-ctl px-3 font-semibold text-primary">{v.state === "draft" ? "Editar" : "Ver"}</GuardedLink>
                        {v.state === "published" && (
                          <Button variant="secondary" loading={busy === `v-${v.id}`} onClick={() => void act(`v-${v.id}`, async () => {
                            const res = await post<{ data: VersionDetail }>(`${base}/templates/${t.id}/versions`, { based_on_version_id: v.id });
                            go(editorHref(t.id, res.data.id));
                          }, "Nova versão criada em rascunho.")}>Nova versão a partir desta</Button>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </Panel>
            </li>
          ))}
        </ul>
      )}
    </MaturityLayout>
  );
}

function NewTemplateDialog({ institution, templates, onClose, onCreated }: {
  institution: MaturityInstitution; templates: TemplateSummary[]; onClose: () => void; onCreated: (t: TemplateSummary) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [source, setSource] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const published = templates.flatMap((t) => t.versions.filter((v) => v.state === "published").map((v) => ({ t, v })));
  async function create() {
    if (!name.trim()) return setError("Informe o nome do modelo.");
    setBusy(true);
    try {
      const res = await post<{ data: TemplateSummary }>(`${mBase(institution.id)}/templates`, { name: name.trim(), source_template_version_id: source || null });
      await qc.invalidateQueries({ queryKey: mKey(institution.id, "templates") });
      onCreated(res.data);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Não foi possível criar o modelo.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="Novo modelo de questionário" description="O modelo começa como rascunho; publique a versão para usá-la em avaliações."
      footer={<><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button loading={busy} onClick={() => void create()}>Criar modelo</Button></>}>
      {error && <Alert tone="danger">{error}</Alert>}
      <TextField label="Nome do modelo" required autoFocus maxLength={MATURITY_LIMITS.name} value={name} onChange={(e) => { setName(e.target.value); setError(null); }} />
      <SelectField label="Começar a partir de" value={source} onChange={(e) => setSource(e.target.value)}>
        <option value="">Modelo em branco (só a dimensão Gestão de Pessoas)</option>
        {published.map(({ t, v }) => <option key={v.id} value={v.id}>{t.name} · versão {v.revision_number}</option>)}
      </SelectField>
    </Modal>
  );
}
